// Diccionarios de mapeo para conversión de números a letras
type UnidadKey = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
type DecenaKey = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
type CentenaKey = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9

const UNIDADES_MAP: Record<UnidadKey, string> = {
  0: '',
  1: 'Uno',
  2: 'Dos',
  3: 'Tres',
  4: 'Cuatro',
  5: 'Cinco',
  6: 'Seis',
  7: 'Siete',
  8: 'Ocho',
  9: 'Nueve'
}

const DECENAS_ESPECIALES: Record<number, string> = {
  10: 'Diez',
  11: 'Once',
  12: 'Doce',
  13: 'Trece',
  14: 'Catorce',
  15: 'Quince'
}

/**
 * Convierte un número de 0-9 a su representación en letras
 */
export const Unidades = (num: number): string => {
  // Number.isInteger descarta NaN y decimales. Sin esto, `num < 0 || num > 9` NO atrapa
  // NaN (toda comparación con NaN es false), y `UNIDADES_MAP[NaN]` devolvería undefined,
  // rompiendo aguas abajo en Decenas con `Unidades(...).toLowerCase()`.
  if (!Number.isInteger(num) || num < 0 || num > 9) return ''
  return UNIDADES_MAP[num as UnidadKey]
}

/**
 * Construye decenas con 'y' (treinta y uno, cuarenta y dos, etc.)
 */
const DecenasY = (strSin: string, numUnidades: number): string => {
  if (numUnidades > 0) {
    return `${strSin} Y ${Unidades(numUnidades)}`
  }
  return strSin
}

/**
 * Convierte un número de 0-99 a su representación en letras
 */
export const Decenas = (num: number): string => {
  if (num < 10) return Unidades(num)
  if (num < 16) return DECENAS_ESPECIALES[num] || ''

  const decena = Math.floor(num / 10)
  const unidad = num % 10

  const decenasMap: Record<DecenaKey, string> = {
    0: Unidades(unidad),
    1: unidad <= 5 ? DECENAS_ESPECIALES[num] : `Dieci${Unidades(unidad).toLowerCase()}`,
    2: unidad === 0 ? 'Veinte' : `Veinti${Unidades(unidad).toLowerCase()}`,
    3: DecenasY('Treinta', unidad),
    4: DecenasY('Cuarenta', unidad),
    5: DecenasY('Cincuenta', unidad),
    6: DecenasY('Sesenta', unidad),
    7: DecenasY('Setenta', unidad),
    8: DecenasY('Ochenta', unidad),
    9: DecenasY('Noventa', unidad)
  }

  return decenasMap[decena as DecenaKey] || ''
}

/**
 * Convierte un número de 0-999 a su representación en letras
 */
const Centenas = (num: number): string => {
  if (num < 100) return Decenas(num)

  const centenas = Math.floor(num / 100)
  const decenas = num % 100

  const centenasMap: Record<CentenaKey, string> = {
    0: Decenas(decenas),
    1: decenas > 0 ? `Ciento ${Decenas(decenas)}` : 'Cien',
    2: `Doscientos ${Decenas(decenas)}`,
    3: `Trescientos ${Decenas(decenas)}`,
    4: `Cuatrocientos ${Decenas(decenas)}`,
    5: `Quinientos ${Decenas(decenas)}`,
    6: `Seiscientos ${Decenas(decenas)}`,
    7: `Setecientos ${Decenas(decenas)}`,
    8: `Ochocientos ${Decenas(decenas)}`,
    9: `Novecientos ${Decenas(decenas)}`
  }

  return centenasMap[centenas as CentenaKey] || Decenas(decenas)
}

/**
 * Maneja secciones de números (miles, millones, billones, etc.)
 */
const Seccion = (num: number, divisor: number, strSingular: string, strPlural: string): string => {
  const cientos = Math.floor(num / divisor)

  if (cientos === 0) return ''
  if (cientos === 1) return strSingular

  return `${Centenas(cientos)} ${strPlural}`
}

/**
 * Convierte números de miles (1,000 - 999,999)
 */
const Miles = (num: number): string => {
  const divisor = 1000
  const resto = num % divisor

  const strMiles = Seccion(num, divisor, 'Un mil', 'Mil')
  const strCentenas = Centenas(resto)

  if (strMiles === '') return strCentenas
  if (strCentenas === '') return strMiles

  return `${strMiles} ${strCentenas}`
}

/**
 * Convierte números de millones (1,000,000 - 999,999,999)
 */
const Millones = (num: number): string => {
  const divisor = 1000000
  const resto = num % divisor

  const strMillones = Seccion(num, divisor, 'Un millon', 'millones')
  const strMiles = Miles(resto)

  if (strMillones === '') return strMiles
  if (strMiles === '') return strMillones

  return `${strMillones} ${strMiles}`
}

/**
 * Convierte números de miles de millones (1,000,000,000 - 999,999,999,999)
 */
const MilesMillones = (num: number): string => {
  const divisor = 1000000000
  const resto = num % divisor

  const setMilMillones = Seccion(num, divisor, 'Un mil millon', 'mil millones')
  const setMillones = Millones(resto)

  if (setMilMillones === '') return setMillones
  if (setMillones === '') return setMilMillones

  return `${setMilMillones} ${setMillones}`
}

/**
 * Convierte números de billones (1,000,000,000,000 - 999,999,999,999,999)
 */
const Billones = (num: number): string => {
  const divisor = 1000000000000
  const resto = num % divisor

  const setBillones = Seccion(num, divisor, 'Un billon', 'billones')
  const setMilMillones = MilesMillones(resto)

  if (setBillones === '') return setMilMillones
  if (setMilMillones === '') return setBillones

  return `${setBillones} ${setMilMillones}`
}

/**
 * Convierte números de trillones (hasta 999,999,999,999,999,999)
 */
const Trillones = (num: number): string => {
  const divisor = 1000000000000000
  const resto = num % divisor

  const setTrillones = Seccion(num, divisor, 'Un trillon', 'trillones')
  const setBillones = Billones(resto)

  if (setTrillones === '') return setBillones
  if (setBillones === '') return setTrillones

  return `${setTrillones} ${setBillones}`
}

/**
 * Convierte un número a su representación en letras en español
 *
 * @param num - Número a convertir (puede ser string o number)
 * @param considerLeadingZero - Si es true, agrega "Cero" al inicio cuando el número comienza con 0
 * @returns Representación en letras del número
 *
 * @example
 * numberToLetters(123) // "Ciento Veinti tres"
 * numberToLetters(1000) // "Un mil"
 * numberToLetters(1.5) // "Uno Punto Cincuenta"
 * numberToLetters("0123", true) // "Cero Ciento Veinti tres"
 */
export const numberToLetters = (num: string | number, considerLeadingZero = false): string => {
  let strNum = String(num)
  let result = ''

  // Manejo de ceros al inicio
  if (considerLeadingZero && strNum.startsWith('0')) {
    result += 'Cero '
    strNum = strNum.substring(1)
  }

  const numeroFloat = Number(strNum)
  // Entrada no numérica (típicamente `Number(undefined)` = NaN por el patrón `Number(x) ?? 0`
  // en callers) → tratar como 0 en vez de propagar NaN.
  if (!Number.isFinite(numeroFloat)) return result ? result.trim() : 'Cero'
  const enteros = Math.floor(numeroFloat)
  const decimales = Math.round(numeroFloat * 100) - enteros * 100

  // Construcción de parte entera
  let letrasEnteros = ''
  if (enteros === 0) {
    letrasEnteros = 'Cero'
  } else {
    letrasEnteros = Trillones(enteros)
  }

  // Construcción de parte decimal
  const letrasDecimales = decimales > 0 ? ` Punto ${Trillones(decimales)}` : ''

  return result + letrasEnteros + letrasDecimales
}

/**
 * Convierte un monto numérico a su representación en letras con formato de moneda
 *
 * @param amount - Monto a convertir (puede ser string o number)
 * @param currency - Moneda a usar (singular y plural)
 * @param decimalCurrency - Nombre de la fracción decimal (singular y plural)
 * @returns Representación en letras del monto con formato de moneda
 *
 * @example
 * numberToLettersCurrency(942428.57, { singular: 'Quetzal', plural: 'Quetzales' }, { singular: 'Centavo de Quetzal', plural: 'Centavos de Quetzal' })
 * // "NOVECIENTOS CUARENTA Y DOS MIL CUATROCIENTOS VEINTIOCHO QUETZALES CON CINCUENTA Y SIETE CENTAVOS DE QUETZAL"
 *
 * @example
 * numberToLettersCurrency(1500.00, { singular: 'Dólar', plural: 'Dólares' }, { singular: 'Centavo de Dólar', plural: 'Centavos de Dólar' })
 * // "MIL QUINIENTOS DÓLARES CON CERO CENTAVOS DE DÓLAR"
 */
export const numberToLettersCurrency = (
  amount: string | number,
  currency: { singular: string; plural: string },
  decimalCurrency: { singular: string; plural: string }
): string => {
  const numeroFloat = Number(amount)

  if (isNaN(numeroFloat)) {
    return ''
  }

  const enteros = Math.floor(numeroFloat)
  const decimales = Math.round((numeroFloat - enteros) * 100)

  // Parte entera
  let letrasEnteros = ''
  if (enteros === 0) {
    letrasEnteros = 'Cero'
  } else if (enteros === 1) {
    letrasEnteros = 'Un'
  } else {
    letrasEnteros = Trillones(enteros)
  }

  // Determinar si usar singular o plural para la moneda principal
  const nombreMoneda = enteros === 1 ? currency.singular : currency.plural

  // Parte decimal
  let letrasDecimales = ''
  if (decimales === 0) {
    letrasDecimales = 'Cero'
  } else if (decimales === 1) {
    letrasDecimales = 'Un'
  } else {
    letrasDecimales = Trillones(decimales)
  }

  // Determinar si usar singular o plural para los centavos
  const nombreDecimal = decimales === 1 ? decimalCurrency.singular : decimalCurrency.plural

  return `${letrasEnteros.toUpperCase()} ${nombreMoneda.toUpperCase()} CON ${letrasDecimales.toUpperCase()} ${nombreDecimal.toUpperCase()}`
}

/**
 * Convierte un número a letras forzando SIEMPRE la parte decimal, pensado para
 * áreas (m2) en contratos: "veinticinco punto cero ..." aunque los decimales sean 0.
 *
 * A diferencia de `numberToLetters`, que omite los decimales cuando son 0,
 * esta función siempre los muestra. La parte decimal se lee como número de
 * `decimals` dígitos (ej. .56 -> "Cincuenta y Seis", .00 -> "Cero").
 *
 * @param value - Número a convertir (string o number)
 * @param decimals - Cantidad de decimales a considerar (default 2)
 *
 * @example
 * numberToLettersDecimal(25)    // "Veinticinco Punto Cero"
 * numberToLettersDecimal(2.56)  // "Dos Punto Cincuenta y Seis"
 * numberToLettersDecimal(25.5)  // "Veinticinco Punto Cincuenta"
 */
export const numberToLettersDecimal = (value: string | number, decimals = 2): string => {
  const numeroFloat = Number(value)

  if (isNaN(numeroFloat)) {
    return ''
  }

  const enteros = Math.floor(numeroFloat)
  const factor = 10 ** decimals
  const parteDecimal = Math.round((numeroFloat - enteros) * factor)

  const letrasEnteros = numberToLetters(enteros)
  const letrasDecimales = numberToLetters(parteDecimal)

  return `${letrasEnteros} Punto ${letrasDecimales}`
}
